import { ButtonType, ButtonWidget } from '@tableau/widgets';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { requestAvailableActionsForEntity } from '../../../app/actions/spaces/AvailableActionsForEntityActions';
import { shareContent } from '../../../app/actions/spaces/ShareContentAction';
import { selectAvailableActionsForEntity } from '../../../app/selectors/EntityActionsSelectors';
import { ShareContentAction } from '../../../app/spaces/ActionsMenu/EntityActions/ShareContentAction';
import { MapDispatchToProps, MapStateToProps } from '../../../app/utils/ConnectUtils';
import { Messages } from '../../../codegen/Localize';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { IMetricActions } from '../../../ts/ServerApi/types/IMetricActions';
import { IMetricInfoWithoutData } from '../../models/IMetricInfo';

interface MetricDetailShareButtonStateProps {
  isVisible: boolean;
  isClickable: boolean;
}

interface MetricDetailShareButtonDispatchProps {
  requestAvailableActionsForEntity: (entity: IExplorableItemTypeInfo) => void;
  shareContent: (content: IContentInfo) => void;
}

interface MetricDetailShareButtonOwnProps {
  metricInfo: IMetricInfoWithoutData;
}

type MetricDetailShareButtonProps = MetricDetailShareButtonStateProps & MetricDetailShareButtonDispatchProps & MetricDetailShareButtonOwnProps;

export const MetricDetailShareButton = (props: MetricDetailShareButtonProps): JSX.Element => {
  React.useEffect(
    () => {
      props.requestAvailableActionsForEntity(props.metricInfo);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.requestAvailableActionsForEntity, props.metricInfo]
  );

  if (!props.isVisible)
    return null;

  const getContent = () => {
    return (
      <div style={buttonLayoutStyle}>
        <img style={iconLayoutStyle} src={imgPath('share_inverse.svg')} alt={Messages.shareMetricIconAltText()} />
        <span>{Messages.shareMetricButtonLabel()}</span>
      </div>
    );
  };

  const onClick = () => {
    props.shareContent(props.metricInfo);
  };

  return (
    <ButtonWidget
      buttonType={ButtonType.Primary}
      disabled={!props.isClickable}
      testId={'metric-detail-share'}
      onClick={onClick}
      getContent={getContent}
      style={{cursor: !props.isClickable ? undefined : 'pointer'}}
    />
  );
};

const buttonLayoutStyle = {
  display: 'flex',
  alignItems: 'center',
};

const iconLayoutStyle = {
  marginRight: '8px',
};

const mapStateToProps: MapStateToProps<MetricDetailShareButtonStateProps, MetricDetailShareButtonOwnProps> = (state, ownProps) => {
  const availableActions: IMetricActions = selectAvailableActionsForEntity(state, ownProps.metricInfo) as IMetricActions;
  return {
    isVisible: ShareContentAction.isVisible([ownProps.metricInfo], state),
    isClickable: ShareContentAction.isClickable(availableActions, [ownProps.metricInfo], state),
  };
};

const mapDispatchToProps: MapDispatchToProps<MetricDetailShareButtonDispatchProps> = (dispatch) => bindActionCreators({
  requestAvailableActionsForEntity: requestAvailableActionsForEntity,
  shareContent: shareContent
}, dispatch);

export const ConnectedMetricDetailShareButton: React.ComponentClass<MetricDetailShareButtonOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(MetricDetailShareButton);
